import Bugsnag from '@bugsnag/js'
import { ApiError } from '@legacy/externalApi/ApiResponse'

export const buildAuthenticatedHeaders = (token: string) => {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

/**
 * This utility function is used to handle errors from the fetch api.
 *
 * @param apiResponse The response from the API
 * @param setApiError A function to set the API error. This is generally used for errors that are not 422, 403 or 404.
 * @param setError A function to set the error. This is generally used for errors that are 422, 403 or 404.
 * @param customErrorStatusBody A custom error body that will override the default depending on the status code.
 * @param throwOn If true, the function with throw errors for the given status code
 * @param callback A callback function that is mostly used by functions that directly interact with RFF. Example: apps/team/app/routes/policyContainers/cancel.tsx
 * @returns The response from the API
 */
export const handleError = async ({
  apiResponse,
  setApiError,
  setError,
  customErrorStatusBody,
  throwOn,
  callback,
}: {
  apiResponse: Response
  setApiError: (error: any) => void
  setError?: (error: any) => void
  customErrorStatusBody?: Record<number, any>
  throwOn?: Record<number, boolean>
  callback?: (error: any) => void
}) => {
  let response: ApiError
  if (throwOn?.[apiResponse.status]) {
    try {
      response = new ApiError(apiResponse, await apiResponse.json())
      throw new Error(JSON.stringify(response.body))
    } catch (error) {
      // Only handle JSON parsing errors specially
      if (error instanceof SyntaxError) {
        // This is expected to trigger for API responses that are not JSON
        Bugsnag.notify(error as any)
        throw new Error(apiResponse.statusText)
      }
      // For other errors, just throw them as they are
      throw error
    }
  }

  try {
    if (![422, 403, 404].includes(apiResponse.status)) {
      response = new ApiError(apiResponse)
      setApiError(response)
    } else {
      response = new ApiError(apiResponse, await apiResponse.json())
    }

    if (customErrorStatusBody?.[apiResponse.status]) {
      response.body = customErrorStatusBody[apiResponse.status]
    }

    setError?.(response.body)
    callback?.(response.body)

    return response
  } catch (error) {
    // This catch is expected to trigger for API responses that are not JSON
    Bugsnag.notify(error as any)
    const response = new ApiError(apiResponse)
    setApiError(response)
    return response
  }
}
